import { useEffect } from "react";
import { useSelector } from "react-redux";

import { useQuery } from "@tanstack/react-query";

import { getAnalysisDatasetsV2 } from "@/api/analysis/analysisApi";
import { RootState } from "@/store";
import { EOutputAnalysisType } from "@/types/spatial";

import { useDataset } from "../map/useDataset";

type UseGetAnalysisDatasetsOptions = {
  /**
   * Set to `false` for surfaces that render a dataset they do not own — e.g. the
   * read-only Analysis Config panel, which lists the *input* datasets while the
   * store holds the *result* datasets. There `datasets[datasetId]` would resolve
   * against the wrong source, and a second mount of this hook for an id already
   * rendered by the result panel would run `addAnalysisDatasetGeometry` twice.
   */
  enabled?: boolean;
};

export function useGetAnalysisDatasets(
  datasetId: string,
  options: UseGetAnalysisDatasetsOptions = {}
) {
  const { enabled = true } = options;
  const { datasets } = useSelector((state: RootState) => state.dataset);
  const analysisId = useSelector(
    (state: RootState) => state.dataset.analysisId
  );

  const dataset = datasets[datasetId];

  const { addAnalysisDatasetGeometry } = useDataset();

  const shouldFetch =
    enabled &&
    !!analysisId &&
    !!datasetId &&
    !!dataset?.properties?.isFetching &&
    ![EOutputAnalysisType.PROFILING, EOutputAnalysisType.GRID].includes(
      (dataset?.data?.spatial_aggregation_type ?? "") as EOutputAnalysisType
    );

  const { data, isSuccess, isError, refetch } = useQuery({
    queryKey: ["analysis-datasets", analysisId, datasetId],
    queryFn: () => getAnalysisDatasetsV2(analysisId || "", datasetId),
    enabled: shouldFetch
  });

  useEffect(() => {
    /** `enabled` is re-checked here on purpose: a disabled `useQuery` still
     * reads the cache for its key, so a consumer that opted out would otherwise
     * replay this write as soon as another mount populates the same key. */
    if (enabled && isSuccess && data?.result?.length) {
      addAnalysisDatasetGeometry(data.result[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isSuccess, data?.result]);

  return { isError, refetch };
}
